/**
 * "Translate this cell" (2026-09-04) — the other half of the toolbar's
 * translate icon, PURE (no React, no DOM, no fetch).
 *
 * The column run (F-70) is a server-side job over the whole Original Script
 * column; this is the small, immediate gesture: the cells the writer has
 * selected, translated in ONE request, each answer APPENDED under the cell's
 * own text on a new line. Nothing moves to another column — a writer reading
 * a line in a language they don't speak wants the meaning right there, under
 * the line, not a column away.
 *
 * What this module decides:
 *  - which of the selected cells CAN be sent (`translatableCells`): text
 *    cells with something in them. An image or a BGM stem has no prose;
 * - whether the gesture is offered at all, and why not (`cellTranslateEligibility`);
 *  - the new cell value (`appendTranslation`) — idempotent, so running it
 *    twice on the same cell does not stack the same translation twice;
 *  - the one-line outcome for the toast (`cellTranslateSummary`).
 *
 * The cap (`MAX_CELLS_PER_RUN`) mirrors the server's `TRANSLATE_CELLS_MAX`:
 * a selection, not a column. Past it the menu says "select fewer cells"
 * rather than silently translating the first twenty.
 */
import type { CellsToolbarSelection } from "../../registries/toolbar-actions/index.js";

/** Cells one gesture may translate — the server's `TRANSLATE_CELLS_MAX`. */
export const MAX_CELLS_PER_RUN = 20;

/** One cell as the request carries it; `key` is `rowId:colId`, the reply's key. */
export interface TranslatableCell {
  rowId: string;
  colId: string;
  key: string;
  text: string;
}

/** The reply key for a cell — ids are opaque strings, so both halves are encoded. */
export function cellKey(rowId: string, colId: string): string {
  return `${encodeURIComponent(rowId)}:${encodeURIComponent(colId)}`;
}

/**
 * The selected cells that hold prose, in selection order. `textOf` reads the
 * cell's TEXT (an image renders as "", a BGM stem as its title) and `isProse`
 * says whether the value is text at all — a stem's title is text a reader
 * sees, but not a line anyone wants translated under the music.
 */
export function translatableCells(
  selection: CellsToolbarSelection,
  textOf: (rowId: string, colId: string) => string,
  isProse: (rowId: string, colId: string) => boolean,
): TranslatableCell[] {
  const out: TranslatableCell[] = [];
  for (const rowId of selection.rowIds) {
    for (const colId of selection.colIds) {
      if (!isProse(rowId, colId)) continue;
      const text = textOf(rowId, colId);
      if (text.trim().length === 0) continue;
      out.push({ rowId, colId, key: cellKey(rowId, colId), text });
    }
  }
  return out;
}

export type CellTranslateEligibility =
  | { ok: true; cells: TranslatableCell[] }
  /** Nothing in the selection has text to translate. */
  | { ok: false; reason: "no-text" }
  /** More prose cells than one request may carry. */
  | { ok: false; reason: "too-many"; count: number };

export function cellTranslateEligibility(cells: TranslatableCell[]): CellTranslateEligibility {
  if (cells.length === 0) return { ok: false, reason: "no-text" };
  if (cells.length > MAX_CELLS_PER_RUN) return { ok: false, reason: "too-many", count: cells.length };
  return { ok: true, cells };
}

/** The menu row's subtitle when the gesture is withheld. */
export function cellIneligibleMessage(
  eligibility: Exclude<CellTranslateEligibility, { ok: true }>,
): string {
  switch (eligibility.reason) {
    case "no-text":
      return "Select a cell with text";
    case "too-many":
      return `Select up to ${MAX_CELLS_PER_RUN} cells (${eligibility.count} selected)`;
  }
}

/** The menu row's title: "Translate this cell" / "Translate 3 cells". */
export function cellTranslateTitle(count: number): string {
  return count <= 1 ? "Translate this cell" : `Translate ${count} cells`;
}

/**
 * The cell's new value: its text, then the translation on its own line.
 *
 * Idempotent on purpose: a cell that already ends with this exact translation
 * (the writer hit Translate twice, or a peer got there first) is returned
 * unchanged, so nothing is written and nothing stacks. A translation that
 * comes back empty, or that equals the text (the model was told to return
 * text already in the target language unchanged), is likewise a no-op.
 */
export function appendTranslation(text: string, translation: string): string {
  const added = translation.trim();
  if (added.length === 0) return text;
  const base = text.replace(/\s+$/, "");
  if (base === added || base.endsWith(`\n${added}`)) return text;
  return `${base}\n${added}`;
}

export interface CellTranslateOutcome {
  /** Cells whose value changed. */
  written: number;
  /** Cells the answer left as they were (already translated, or empty). */
  unchanged: number;
  /** Cells whose text changed under us while the request was out. */
  skipped: number;
}

/** The toast, in the user's words. `language` is the display name ("Korean"). */
export function cellTranslateSummary(outcome: CellTranslateOutcome, language: string): string {
  const { written, unchanged, skipped } = outcome;
  const cells = (n: number) => (n === 1 ? "1 cell" : `${n} cells`);
  if (written === 0 && skipped === 0) {
    return unchanged === 1
      ? `This cell already holds its ${language} translation`
      : `These cells already hold their ${language} translation`;
  }
  const parts = [`Translated ${cells(written)} into ${language}`];
  if (skipped > 0) parts.push(`${cells(skipped)} changed meanwhile and ${skipped === 1 ? "was" : "were"} left alone`);
  return parts.join(" — ");
}
